import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import GraphTemplate from "Components/templates/graph-template"
import ActionButton from "Components/atoms/buttons/action-button"
import Chip from "Components/atoms/chip"
import { SyncIcon } from "Components/atoms/icons"
import SelectInput from "Components/molecule/inputs/select-input"
import TextInput from "Components/molecule/inputs/text-input"
import InfoTooltip from "Components/molecule/info-tooltip"
import LambdaRunStrip from "Components/molecule/lambda-run-strip"
import LambdaRunModal from "Components/organisms/lambda-run-modal"
import LambdaInventoryTable from "Components/organisms/lambda-inventory-table"
import LambdaScreenDependencies from "Components/organisms/lambda-screen-dependencies"
import LambdaRunDialog from "Components/organisms/lambda-run-dialog"
import LambdaDependencyEditor from "Components/organisms/lambda-dependency-editor"
import LambdaRunsPanel from "Components/organisms/lambda-runs-panel"
import Tabs from "Components/molecule/tabs"
import LoadingState from "Components/organisms/loading-state"
import { LambdaDependencyOptions, LambdaRun, LambdaRunOptions, LambdaRunsResult, LambdaScreenDependenciesResult, LambdaTrackerItem, LambdaTrackerUI, ScreenDependency } from "models/lambda-tracker"
import { AdminLambdaTrackerSliceState, setLambdaTracker } from "slices/admin/lambda-tracker/reducer"
import { cancelLambdaRun, getLambdaDependencyOptions, getLambdaRuns, getLambdaScreenDependencies, getLambdaTracker, runAllLambdas, runLambda, saveScreenDependencies } from "slices/admin/lambda-tracker/thunk"
import { formatDateTimeAmPm } from "utils/tools"
import { navigateToLambdaTrackerDetails } from "utils/navigator"
import withRouter from "Components/Common/withRouter"

const selectLambdaTracker = createSelector(
    (state: any) => state.AdminLambdaTracker,
    ({ result, days }: AdminLambdaTrackerSliceState) => ({ result, days })
)

const DAY_OPTIONS = [
    { value: "1", label: "Last 24 hours" },
    { value: "7", label: "Last 7 days" },
    { value: "14", label: "Last 14 days" },
    { value: "30", label: "Last 30 days" },
]

const FILTER_OPTIONS = [
    { value: "all", label: "All Lambdas" },
    { value: "problems", label: "Only with problems" },
]

// Acumatica, Dayforce and IbexIQ, in that order; anything else after.
const SYSTEM_ORDER = ["Acumatica", "Dayforce", "IbexIQ"]

const RUNS_IN_STRIP = 48
/** How often manual runs are re-read while one is queued or running. */
const RUNS_POLL_MS = 10000

const TAB_STATUS = "Status"
const TAB_INVENTORY = "Inventory"
const TAB_DEPENDENCIES = "Screen dependencies"
const TAB_RUNS = "Manual runs"
const TABS = [TAB_STATUS, TAB_INVENTORY, TAB_DEPENDENCIES, TAB_RUNS]

type RunTarget = { mode: "single", item: LambdaTrackerItem } | { mode: "all" }

const LambdaTrackerPage = (props: any) => {
    document.title = "Lambda Tracker | IbexIQ";
    const dispatch: any = useDispatch()
    const { result, days: cachedDays } = useSelector(selectLambdaTracker)

    const [days, setDays] = useState<number>(cachedDays)
    const [isLoading, setIsLoading] = useState<boolean>(!result)
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
    const [search, setSearch] = useState<string>("")
    const [filter, setFilter] = useState<string>("all")
    const [selected, setSelected] = useState<{ run: LambdaRun, item: LambdaTrackerItem } | null>(null)
    const [activeTab, setActiveTab] = useState<string>(TAB_STATUS)
    const [dependencies, setDependencies] = useState<LambdaScreenDependenciesResult | null>(null)
    const [isLoadingDependencies, setIsLoadingDependencies] = useState<boolean>(false)
    const [runs, setRuns] = useState<LambdaRunsResult | null>(null)
    const [runTarget, setRunTarget] = useState<RunTarget | null>(null)
    const wasRunning = useRef<boolean>(false)
    // Dependency editor: undefined = closed, null = adding a screen
    const [editingScreen, setEditingScreen] = useState<ScreenDependency | null | undefined>(undefined)
    const [dependencyOptions, setDependencyOptions] = useState<LambdaDependencyOptions | null>(null)

    const openDependencyEditor = async (screen: ScreenDependency | null) => {
        setEditingScreen(screen)
        if (!dependencyOptions) setDependencyOptions(await getLambdaDependencyOptions())
    }

    const saveDependencies = async (screenCode: string, lambdaIds: string[]) => {
        const ok = await saveScreenDependencies(screenCode, lambdaIds)
        if (ok) await loadDependencies(days)
        return ok
    }

    const load = async (windowDays: number) => {
        const res = await getLambdaTracker(windowDays)
        if (res) dispatch(setLambdaTracker({ result: res, days: windowDays }))
    }

    // Screen dependencies are loaded the first time their tab is opened, and again on refresh / period change.
    const loadDependencies = async (windowDays: number) => {
        setIsLoadingDependencies(true)
        setDependencies(await getLambdaScreenDependencies(windowDays))
        setIsLoadingDependencies(false)
    }

    const loadRuns = async () => {
        const res = await getLambdaRuns()
        if (res) setRuns(res)
        return res
    }

    useEffect(() => {
        const init = async () => {
            await Promise.all([
                !result || cachedDays !== days ? load(days) : Promise.resolve(),
                loadRuns(),
            ])
            setIsLoading(false)
        }
        init()
    }, [])

    useEffect(() => {
        if (activeTab === TAB_DEPENDENCIES && !dependencies && !isLoadingDependencies) loadDependencies(days)
    }, [activeTab])

    const hasActiveRun = !!runs?.batches.some(b => b.status === "Queued" || b.status === "Running")

    // While a manual run is active, poll it; when it finishes, reload the tracker to show the new runs.
    useEffect(() => {
        if (hasActiveRun) {
            wasRunning.current = true
            const timer = setInterval(loadRuns, RUNS_POLL_MS)
            return () => clearInterval(timer)
        }
        if (wasRunning.current) {
            wasRunning.current = false
            load(days)
            setDependencies(null)
        }
    }, [hasActiveRun])

    const changeDays = async (value: number) => {
        setDays(value)
        setIsRefreshing(true)
        setDependencies(null)
        await load(value)
        if (activeTab === TAB_DEPENDENCIES) await loadDependencies(value)
        setIsRefreshing(false)
    }

    const refresh = async () => {
        setIsRefreshing(true)
        await Promise.all([load(days), loadRuns()])
        if (activeTab === TAB_DEPENDENCIES) await loadDependencies(days)
        else setDependencies(null)
        setIsRefreshing(false)
    }

    const startRun = async (options: LambdaRunOptions): Promise<boolean> => {
        if (!runTarget) return false
        const batch = runTarget.mode === "all"
            ? await runAllLambdas(options)
            : await runLambda(runTarget.item.id, options)
        if (!batch) return false
        await loadRuns()
        setActiveTab(TAB_RUNS)
        return true
    }

    const cancelRun = async (batchId: string) => {
        if (await cancelLambdaRun(batchId)) await loadRuns()
    }

    const items = useMemo(() => result?.items ?? [], [result])
    const environment = result?.environment
    const lambdasEnabled = !!environment?.lambdasEnabled
    // Active Lambdas with nothing to compare their runs against (a disabled schedule is intentional)
    const unscheduled = useMemo(() => items.filter(i => i.isActive && i.schedules.length === 0), [items])

    const summary = useMemo(() => {
        const active = items.filter(i => i.isActive)
        const cost = active.reduce((sum, i) => sum + (i.stats.totalCostUsd ?? 0), 0)
        return {
            healthy: active.filter(i => i.currentHealth === "green").length,
            warning: active.filter(i => i.currentHealth === "yellow").length,
            failing: active.filter(i => i.currentHealth === "red").length,
            runs: active.reduce((sum, i) => sum + i.stats.totalRuns, 0),
            runnable: active.filter(i => i.runnable).length,
            cost,
        }
    }, [items])

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase()
        return items.filter(i =>
            (!term || i.displayName.toLowerCase().includes(term) || i.code.toLowerCase().includes(term)
                || (i.lambdaFunctionName ?? "").toLowerCase().includes(term))
            && (filter === "all" || i.runs.some(r => r.health !== "green")))
    }, [items, search, filter])

    const groups = useMemo(() => {
        const systems = Array.from(new Set(filtered.map(i => i.externalSystem)))
            .sort((a, b) => {
                const ia = SYSTEM_ORDER.indexOf(a), ib = SYSTEM_ORDER.indexOf(b)
                return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b)
            })

        return systems.map(system => ({
            system,
            items: filtered
                .filter(i => i.externalSystem === system)
                .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.displayName.localeCompare(b.displayName)),
        }))
    }, [filtered])

    const inventoryItems = useMemo(() => groups.flatMap(g => g.items), [groups])

    const openDetails = (item: LambdaTrackerItem) => navigateToLambdaTrackerDetails(props.router.navigate, item.id, days)
    const openDetailsById = (id: string) => navigateToLambdaTrackerDetails(props.router.navigate, id, days)

    const statusTab = (
        <div className="lambda-tracker-status">
            <div className="lambda-tracker-summary">
                <div className="summary-tile tone-green">
                    <i className="mdi mdi-check-circle" aria-hidden />
                    <div><strong>{summary.healthy}</strong><small>Healthy</small></div>
                </div>
                <div className="summary-tile tone-yellow">
                    <i className="mdi mdi-alert" aria-hidden />
                    <div><strong>{summary.warning}</strong><small>Needs attention</small></div>
                </div>
                <div className="summary-tile tone-red">
                    <i className="mdi mdi-close-circle" aria-hidden />
                    <div><strong>{summary.failing}</strong><small>Failing</small></div>
                </div>
                <div className="summary-tile">
                    <i className="mdi mdi-play-circle-outline" aria-hidden />
                    <div><strong>{summary.runs.toLocaleString()}</strong><small>Runs in period</small></div>
                </div>
                <div className="summary-tile">
                    <i className="mdi mdi-currency-usd" aria-hidden />
                    <div>
                        <strong>{LambdaTrackerUI.cost(summary.cost)}</strong>
                        <small>Cost in period <InfoTooltip>Lambda compute (GB-seconds) plus requests, at the on-demand x86 price. Runs without a CloudWatch REPORT line are estimated from their start and end times.</InfoTooltip></small>
                    </div>
                </div>
            </div>

            <div className="lambda-legend" aria-hidden>
                <span><i className="mdi mdi-check-circle health-green" /> Succeeded</span>
                <span><i className="mdi mdi-alert health-yellow" /> Warning or late</span>
                <span><i className="mdi mdi-close-circle health-red" /> Failed, timed out or crashed</span>
                <span><i className="mdi mdi-clock-alert health-red" /> Missed a scheduled run</span>
            </div>

            {groups.length === 0 && (
                <div className="text-center text-muted py-5">No Lambdas match these filters.</div>
            )}

            {groups.map(group => (
                <section key={group.system} className="lambda-group">
                    <h5 className="lambda-group-title">{group.system} <span className="text-muted">({group.items.length})</span></h5>
                    <div className="lambda-rows">
                        {group.items.map(item => (
                            <div key={item.id} className={`lambda-row ${item.isActive ? "" : "inactive"}`}>
                                <div className="lambda-row-name">
                                    <i className={`${LambdaTrackerUI.healthIcon(item.currentHealth)} health-${item.currentHealth ?? "none"} fs-4`}
                                        title={LambdaTrackerUI.healthLabel(item.currentHealth)} aria-hidden />
                                    <div>
                                        <button type="button" className="btn btn-link p-0 lambda-name" onClick={() => openDetails(item)}>
                                            {item.displayName}
                                        </button>
                                        <small className="d-block text-muted">
                                            {item.functionName ?? item.lambdaFunctionName ?? item.code}
                                            {!item.isActive && <> · <Chip variant="secondary">Inactive</Chip></>}
                                        </small>
                                        <small className="d-block text-muted">
                                            {item.manualRunActive
                                                ? <span className="text-info"><i className="mdi mdi-loading mdi-spin me-1" aria-hidden />Manual run in progress</span>
                                                : item.nextExpectedAt
                                                    ? <>Next: {formatDateTimeAmPm(item.nextExpectedAt)}</>
                                                    : <>{LambdaTrackerUI.schedules(item)}</>}
                                        </small>
                                    </div>
                                </div>
                                <div className="lambda-row-strip">
                                    <LambdaRunStrip runs={item.runs} max={RUNS_IN_STRIP} onSelect={run => setSelected({ run, item })} />
                                </div>
                                <div className="lambda-row-stats">
                                    <div><small>Success</small><strong>{LambdaTrackerUI.percent(item.stats.successRate)}</strong></div>
                                    <div><small>Avg time</small><strong>{LambdaTrackerUI.duration(item.stats.avgMs)}</strong></div>
                                    <div><small>Cost</small><strong>{LambdaTrackerUI.cost(item.stats.totalCostUsd)}</strong></div>
                                </div>
                                <div className="lambda-row-actions">
                                    {item.runnable && (
                                        <button type="button" className="icon-button" title={`Run ${item.displayName} now`} aria-label={`Run ${item.displayName} now`}
                                            disabled={hasActiveRun} onClick={() => setRunTarget({ mode: "single", item })}>
                                            <i className="mdi mdi-play-circle-outline fs-4" />
                                        </button>
                                    )}
                                    <button type="button" className="icon-button" onClick={() => openDetails(item)} aria-label={`Open ${item.displayName}`}>
                                        <i className="mdi mdi-chevron-right fs-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )

    return (
        <GraphTemplate
            title="Admin"
            breadcrumbItem="Lambda Tracker"
            isLoading={isLoading}
            actions={
                <div className="d-flex gap-2">
                    {lambdasEnabled && (
                        <ActionButton onClick={() => setRunTarget({ mode: "all" })} disabled={hasActiveRun}
                            title={hasActiveRun ? "A manual run is in progress" : "Run every Lambda: sources first, then reports"}>
                            {hasActiveRun ? "Running…" : "Update all"} <i className="mdi mdi-play" />
                        </ActionButton>
                    )}
                    <ActionButton onClick={refresh} disabled={isRefreshing}>
                        {isRefreshing ? "Refreshing…" : "Refresh"} <SyncIcon />
                    </ActionButton>
                </div>
            }
            filters={
                <div className="lambda-tracker-filters">
                    <SelectInput
                        label="Period"
                        options={DAY_OPTIONS}
                        value={String(days)}
                        onChange={e => changeDays(Number(e.target.value))}
                        disabled={isRefreshing}
                    />
                    <SelectInput
                        label="Show"
                        options={FILTER_OPTIONS}
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                    />
                    <TextInput
                        label="Search"
                        placeholder="Name, code or function"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
            }
            modal={<>
                <LambdaRunModal
                    run={selected?.run ?? null}
                    lambdaName={selected?.item.displayName ?? ""}
                    functionName={selected?.item.functionName ?? null}
                    onClose={() => setSelected(null)}
                />
                {runTarget && (
                    <LambdaRunDialog
                        isOpen={!!runTarget}
                        onClose={() => setRunTarget(null)}
                        target={runTarget.mode === "all"
                            ? { mode: "all", count: summary.runnable }
                            : { mode: "single", name: runTarget.item.displayName, kind: runTarget.item.runKind }}
                        onSubmit={startRun}
                    />
                )}
                <LambdaDependencyEditor
                    isOpen={editingScreen !== undefined}
                    onClose={() => setEditingScreen(undefined)}
                    options={dependencyOptions}
                    screen={editingScreen ? { code: editingScreen.screenCode, name: editingScreen.screenName } : null}
                    initialLambdaIds={editingScreen ? editingScreen.lambdas.map(l => l.id) : []}
                    configuredScreenCodes={dependencies?.screens.map(s => s.screenCode) ?? []}
                    onSave={saveDependencies}
                />
            </>}
        >
            <div className={`lambda-tracker ${isRefreshing ? "is-refreshing" : ""}`}>
                {environment && !environment.lambdasEnabled && (
                    <div className="alert alert-secondary d-flex gap-2 align-items-start mb-0" role="status">
                        <i className="mdi mdi-information-outline fs-4" aria-hidden />
                        <div>
                            <strong>No Lambda writes to this environment's database.</strong> Manual runs and CloudWatch figures are off here; runs come from SyncLog only and missed runs are checked against the schedules saved in IbexIQ.
                        </div>
                    </div>
                )}
                {unscheduled.length > 0 && (
                    <div className="alert alert-warning d-flex gap-2 align-items-start mb-0" role="alert">
                        <i className="mdi mdi-calendar-remove fs-4" aria-hidden />
                        <div>
                            <strong>{unscheduled.length} active Lambda{unscheduled.length === 1 ? " has" : "s have"} no expected schedule</strong>, so missed runs are not checked for {unscheduled.length === 1 ? "it" : "them"}: {unscheduled.map(i => i.displayName).join(", ")}. Set a schedule on the detail page.
                        </div>
                    </div>
                )}
                {environment?.lambdasEnabled && !environment.schedules.available && (
                    <div className="alert alert-warning d-flex gap-2 align-items-start mb-0" role="alert">
                        <i className="mdi mdi-calendar-alert fs-4" aria-hidden />
                        <div>
                            <strong>EventBridge schedules could not be read.</strong> Missed runs are checked against the schedules saved in IbexIQ instead.
                            <div><small className="text-muted">{environment.schedules.error}</small></div>
                        </div>
                    </div>
                )}
                {result && !result.cloudWatch.available && (
                    <div className="alert alert-warning d-flex gap-2 align-items-start mb-0" role="alert">
                        <i className="mdi mdi-cloud-alert fs-4" aria-hidden />
                        <div>
                            <strong>CloudWatch Logs could not be read.</strong> Runs, statuses and errors come from SyncLog only:
                            billed duration and cost are estimated, and timeouts or crashes that never wrote a SyncLog row are not shown.
                            <div><small className="text-muted">{result.cloudWatch.errors.join(" · ")}</small></div>
                        </div>
                    </div>
                )}

                <Tabs headers={TABS} activeTab={activeTab} setActiveTab={setActiveTab} tabs={{
                    [TAB_STATUS]: statusTab,
                    [TAB_INVENTORY]: <LambdaInventoryTable items={inventoryItems} days={days} onOpen={openDetails} />,
                    [TAB_DEPENDENCIES]: isLoadingDependencies || !dependencies
                        ? <LoadingState title="Loading dependencies" subtitle="Calculating refresh times…" />
                        : <LambdaScreenDependencies screens={dependencies.screens} onOpenLambda={openDetailsById} onEdit={openDependencyEditor} />,
                    [TAB_RUNS]: runs
                        ? <LambdaRunsPanel batches={runs.batches} lambdasEnabled={runs.lambdasEnabled} suffix={runs.suffix} onCancel={cancelRun} />
                        : <LoadingState title="Loading manual runs" subtitle="" />,
                }} />
            </div>
        </GraphTemplate>
    )
}

export default withRouter(LambdaTrackerPage)
